import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@cutilei/ui'
import { useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Phone from 'services/Phone'
import Address from 'services/Address'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { isValid as isValidCNPJ, format as formatCNPJ } from '@fnando/cnpj'
import { format as formatCPF } from '@fnando/cpf'
import { FaStar } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Container,
  Image,
  Row,
  RatingContainer,
  SubTitle,
  InfoText,
  LinkText,
  Block,
  Button
} from './styles'

export default function BusinessDetailsModal ({
  visible,
  data: business,
  onClose: handleClose = () => null
}) {
  const navigate = useNavigate ()
  const [loading, setLoading] = useState (true)
  const [workingDays, setWorkingDays] = useState ([])
  const token = AuthService.getToken ()

  useEffect (() => {
    if (!business) return
    getData ()
  }, [business])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const getData = async () => {
    try {
      const { data: workingDays } = await cutileiApi.get (
        `/businesses/${business.id}/working_days`, requestConfig
      )

      setWorkingDays (workingDays)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  if (!business) return null
  
  const address = Address.formatAddress (business?.addresses[0])
  const mapsAddress = Address.formatAddressWithNoComplement (business?.addresses[0])
  const addressUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent (mapsAddress)}`

  const handleAccessBusiness = () => {
    AuthService.accessBusiness ({
      id: business.id,
      name: business.name,
      email: business.email,
      logo: business.logo,
      access_level: 'business'
    })
    
    navigate ('/business', { replace: true })
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{business.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {loading ? <Loading/> : (
            <Container>
              <Row style={{position: 'relative', width: 'auto'}}>
                <Image src={business.logo}/>
                {business?.average_rating > 0 && (
                  <RatingContainer>
                    <FaStar size={16} color='#FFC100'/>
                    <SubTitle>
                      {business?.average_rating}
                    </SubTitle>
                  </RatingContainer>
                )}
              </Row>
              <Block>
                <Row>
                  <SubTitle>Razão social: &nbsp;</SubTitle>
                  <InfoText>{business.corporate_name}</InfoText>
                </Row>
                <Row>
                  <SubTitle>Responsável: &nbsp;</SubTitle>
                  <InfoText>{business.owner}</InfoText>
                </Row>
                <Row>
                  <SubTitle>
                    {isValidCNPJ (business.cnpj) ? 'CNPJ' : 'CPF'}: &nbsp;
                  </SubTitle>
                  <InfoText>
                    {isValidCNPJ (business.cnpj) ? formatCNPJ (business.cnpj) : formatCPF (business.cnpj)}
                  </InfoText>
                </Row>
                <Row>
                  <SubTitle>Contato: &nbsp;</SubTitle>
                  <InfoText>{Phone.formatPhone (business.cellphone, true)}</InfoText>
                </Row>
              </Block>
              <Block>
                <SubTitle>Endereço:</SubTitle>
                <LinkText href={addressUrl} target='_blank' rel='noopener noreferrer'>
                  <SubTitle>{address}</SubTitle>
                </LinkText>
              </Block>
              <Block>
                <SubTitle>Dias disponíveis:</SubTitle>
                {workingDays.length > 0 ? workingDays.map (day => (
                  <Row key={day.id}>
                    <InfoText>
                      {day.name}: &nbsp;
                    </InfoText>
                    <SubTitle>
                      {`${DateTime.fromISO (day.opening).toFormat ('H:mm')} - ${DateTime.fromISO (day.closing).toFormat ('H:mm')}`}
                    </SubTitle>
                  </Row>
                )) : (
                  <InfoText>Os dias disponíveis ainda não foram cadastrados</InfoText>
                )}
              </Block>
              <Block>
                <Row>
                  <SubTitle>Cadastro: &nbsp;</SubTitle>
                  <InfoText>
                    {DateTime.fromISO (business.created_at).toFormat ('dd/MM/yyyy - H:mm:ss')}
                  </InfoText>
                </Row>
                <Row>
                  <SubTitle>Última alteração: &nbsp;</SubTitle>
                  <InfoText>
                    {DateTime.fromISO (business.updated_at).toFormat ('dd/MM/yyyy - H:mm:ss')}
                  </InfoText>
                </Row>
              </Block>
              <Row>
                <Button onClick={handleAccessBusiness}>
                  Acessar salão
                </Button>
              </Row>
            </Container>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
